import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../services/supabase';
import styles from '../Auth/Auth.module.scss';
import { StaticPageLayout } from '../StaticPages/StaticPageLayout';
import { PasswordRequirements, isPasswordStrong } from './PasswordRequirements';

export const UpdatePassword: React.FC = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Whether a valid recovery session has been established from the reset link.
  // Starts null = "still checking", then true/false once we know.
  const [hasRecoverySession, setHasRecoverySession] = useState<boolean | null>(null);
  const navigate = useNavigate();

  const passwordStrong = useMemo(() => isPasswordStrong(password), [password]);
  const passwordsMatch = confirmPassword.length === 0 || password === confirmPassword;

  useEffect(() => {
    let resolved = false;

    const markReady = () => {
      resolved = true;
      setHasRecoverySession(true);
      setError(null);
    };

    // The recovery token in the URL (?code=… for PKCE, or #…type=recovery for the
    // legacy implicit flow) is exchanged for a session asynchronously by the
    // supabase client (detectSessionInUrl). Listen for that instead of racing it.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY' || (session && (event === 'SIGNED_IN' || event === 'INITIAL_SESSION'))) {
        markReady();
      }
    });

    // Also check directly in case the session was already established before we subscribed.
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) markReady();
    });

    // If after a grace period nothing has established a session, the link is
    // invalid/expired (or the page was opened directly).
    const timeout = setTimeout(() => {
      if (!resolved) {
        setHasRecoverySession(false);
        setError('Invalid or expired reset link. Please request a new one from the Forgot Password page.');
      }
    }, 3000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!hasRecoverySession) {
      setError('No valid reset session. Please open the link from your password reset email again.');
      return;
    }
    if (!passwordStrong) {
      setError('Please meet all password requirements before continuing.');
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords don't match");
      return;
    }

    setIsSubmitting(true);

    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) {
        setError(updateError.message);
      } else {
        navigate('/');
      }
    } catch {
      setError('An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <StaticPageLayout title="Update Password">
      <div className={styles.authContainer} style={{ minHeight: 'auto', background: 'transparent' }}>
        <div
          className={styles.card}
          style={{ maxWidth: '400px', margin: '0 auto', border: 'none', boxShadow: 'none', background: 'transparent' }}
        >
          <p className={styles.subText}>
            {hasRecoverySession === null
              ? 'Verifying your reset link…'
              : 'Enter your new password below.'}
          </p>

          {error && (
            <div className={styles.error} role="alert" aria-live="polite">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className={styles.form} noValidate>
            <div className={styles.inputGroup}>
              <label htmlFor="password">New Password</label>
              <div className={styles.passwordWrapper}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  autoComplete="new-password"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  aria-describedby="update-password-requirements"
                />
                <button
                  type="button"
                  className={styles.passwordToggle}
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                >
                  {showPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
              <PasswordRequirements password={password} id="update-password-requirements" />
            </div>

            <div className={styles.inputGroup}>
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <input
                type={showPassword ? 'text' : 'password'}
                id="confirmPassword"
                name="confirm-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder="••••••••"
                autoComplete="new-password"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                aria-invalid={!passwordsMatch}
                aria-describedby={!passwordsMatch ? 'update-confirm-error' : undefined}
              />
              {!passwordsMatch && (
                <p
                  id="update-confirm-error"
                  role="alert"
                  aria-live="polite"
                  className={styles.fieldError}
                >
                  Passwords don't match
                </p>
              )}
            </div>

            <button
              type="submit"
              className={styles.button}
              disabled={isSubmitting || !hasRecoverySession || !passwordStrong || !passwordsMatch}
            >
              {isSubmitting ? 'Updating…' : 'Update Password'}
            </button>
          </form>
        </div>
      </div>
    </StaticPageLayout>
  );
};
